/**
 * Módulo para procesar datos del sistema y convertirlos a formato ECF según el tipo de comprobante
 * Este script genera estructuras JSON específicas para cada tipo de comprobante fiscal electrónico
 * basadas en los esquemas XSD correspondientes (31, 32, 33, 34, 41, 43, 44, 45, 46, 47)
 */

/**
 * Función principal que procesa los datos del sistema y genera el JSON correspondiente
 * según el tipo de comprobante fiscal electrónico identificado en el e-NCF
 * @param {Object} systemData - Datos del sistema en formato JSON (data.txt)
 * @returns {Object} - Objeto con todos los JSONs generados para cada tipo de comprobante
 */
function processECFByType(systemData) {
  // Determinar el tipo de e-CF a partir del e-NCF
  let tipoECF = "31"; // Valor predeterminado: Factura de Crédito Fiscal Electrónica

  if (systemData.invoice_ncf && systemData.invoice_ncf.length >= 3) {
    tipoECF = systemData.invoice_ncf.substring(1, 3);
  }

  // Generar el JSON específico según el tipo de comprobante
  const ecfData = generateECFByType(systemData, tipoECF);

  return ecfData;
}

/**
 * Genera el JSON específico según el tipo de comprobante fiscal electrónico
 * @param {Object} systemData - Datos del sistema
 * @param {string} tipoECF - Tipo de comprobante (31, 32, 33, 34, 41, 43, 44, 45, 46, 47)
 * @returns {Object} - JSON específico para el tipo de comprobante
 */
function generateECFByType(systemData, tipoECF) {
  // Funciones auxiliares para formateo de datos
  const formatDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return `${String(date.getDate()).padStart(2, '0')}-${String(date.getMonth() + 1).padStart(2, '0')}-${date.getFullYear()}`;
  };

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) {
      // Si no se proporciona fecha, usar la actual
      const now = new Date();
      return `${String(now.getDate()).padStart(2, '0')}-${String(now.getMonth() + 1).padStart(2, '0')}-${now.getFullYear()} ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`;
    }

    const date = new Date(dateTimeString);
    return `${String(date.getDate()).padStart(2, '0')}-${String(date.getMonth() + 1).padStart(2, '0')}-${date.getFullYear()} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}:${String(date.getSeconds()).padStart(2, '0')}`;
  };

  const formatNumber = (num, decimals = 2) => {
    if (num === null || num === undefined) return null;
    return parseFloat(num).toFixed(decimals);
  };

  // Crear estructura base según el tipo de comprobante
  let ecfData = {};

  // Estructura común para todos los tipos de comprobantes
  const baseStructure = {
    Encabezado: {
      Version: "1.0",
      IdDoc: {
        TipoeCF: tipoECF,
        eNCF: systemData.invoice_ncf || `E${tipoECF}0000000001`,
        FechaVencimientoSecuencia: formatDate(systemData.expire_at) || "31-12-2025",
        IndicadorMontoGravado: "0",
        TipoIngresos: "01", // Ingresos por operaciones (No financieros)
        TipoPago: "1", // Contado
        TablaFormasPago: {
          FormaDePago: []
        },
      },
      Emisor: {
        RNCEmisor: systemData.business_unit_rnc || "",
        RazonSocialEmisor: systemData.business_unit_description || "",
        NombreComercial: systemData.business_unit_description || "",
        DireccionEmisor: systemData.business_unit_address || "",
        FechaEmision: formatDate(systemData.emission_date)
      },
      Comprador: {
        RNCComprador: systemData.entity_document_id || "",
        RazonSocialComprador: systemData.entity_name || ""
      },
      Totales: {
        // Estructura correcta según el esquema XSD oficial
        ValorPagar: formatNumber(systemData.total_amount)
      }
    },
    DetallesItems: {
      Item: []
    },
    FechaHoraFirma: formatDateTime(systemData.created_date)
  };

  // Crear la estructura con ECF como elemento raíz
  ecfData = {
    ECF: baseStructure
  };

  // Personalizar la estructura según el tipo de comprobante
  switch (tipoECF) {
    case "31": // Factura de Crédito Fiscal Electrónica
      ecfData = generate31ECF(systemData, ecfData);
      break;

    case "32": // Factura de Consumo Electrónica
      ecfData = generate32ECF(systemData, ecfData);
      break;

    case "33": // Nota de Débito Electrónica
      ecfData = generate33ECF(systemData, ecfData);
      break;

    case "34": // Nota de Crédito Electrónica
      ecfData = generate34ECF(systemData, ecfData);
      break;

    case "41": // Comprobante Electrónico de Compras
      ecfData = generate41ECF(systemData, ecfData);
      break;

    case "43": // Comprobante Electrónico para Gastos Menores
      ecfData = generate43ECF(systemData, ecfData);
      break;

    case "44": // Comprobante Electrónico para Regímenes Especiales
      ecfData = generate44ECF(systemData, ecfData);
      break;

    case "45": // Comprobante Electrónico Gubernamental
      ecfData = generate45ECF(systemData, ecfData);
      break;

    case "46": // Comprobante Electrónico para Exportaciones
      ecfData = generate46ECF(systemData, ecfData);
      break;

    case "47": // Comprobante Electrónico para Pagos al Exterior
      ecfData = generate47ECF(systemData, ecfData);
      break;

    default:
      // Si no se reconoce el tipo, mantener la estructura base con ECF como raíz
      // ecfData ya está configurado correctamente
      break;
  }

  // Llenar los detalles de ítems comunes para todos los tipos
  ecfData = fillItemDetails(systemData, ecfData);

  // Limpiar la estructura (eliminar campos null, undefined o vacíos)
  ecfData = cleanECFStructure(ecfData);

  return ecfData;
}

/**
 * Genera la estructura específica para Factura de Crédito Fiscal Electrónica (31)
 * @param {Object} systemData - Datos del sistema
 * @param {Object} ecfData - Estructura base del ECF
 * @returns {Object} - Estructura específica para tipo 31
 */
function generate31ECF(systemData, ecfData) {
  const clonedData = JSON.parse(JSON.stringify(ecfData));
  const formatNumber = (num, decimals = 2) => {
    if (num === null || num === undefined) return null;
    return parseFloat(num).toFixed(decimals);
  };

  // Agregar campos específicos para tipo 31 según el esquema XSD oficial
  const subtotalAmount = systemData.subtotal_amount || 0;
  const taxesAmount = systemData.taxes_amount || 0;
  const totalAmount = systemData.total_amount || (parseFloat(subtotalAmount) + parseFloat(taxesAmount));

  const newTotales = {
    MontoGravadoTotal: formatNumber(subtotalAmount),
    MontoGravadoI1: formatNumber(subtotalAmount),
    ITBIS1: "18",
    TotalITBIS: formatNumber(taxesAmount),
    TotalITBIS1: formatNumber(taxesAmount),
    MontoTotal: formatNumber(totalAmount)
  };

  clonedData.ECF.Encabezado.Totales = newTotales;

  if (systemData.entity_contact_json && Array.isArray(systemData.entity_contact_json)) {
    const direccion = systemData.entity_contact_json.find(contact => contact.description_type === "DIRECCION");
    const telefono = systemData.entity_contact_json.find(contact => contact.description_type === "CELULAR");

    if (direccion) {
      clonedData.ECF.Encabezado.Comprador.DireccionComprador = String(direccion.description).substring(0, 100);
    }

    if (telefono) {
      clonedData.ECF.Encabezado.Comprador.TelefonoAdicional = telefono.description;
    }
  }

  return clonedData;
}

/**
 * Genera la estructura específica para Factura de Consumo Electrónica (32)
 * @param {Object} systemData - Datos del sistema
 * @param {Object} baseStructure - Estructura base del ECF
 * @returns {Object} - Estructura específica para tipo 32
 */
function generate32ECF(systemData, baseStructure) {
  const ecfData = JSON.parse(JSON.stringify(baseStructure)); // Clonar estructura base
  const formatNumber = (num, decimals = 2) => {
    if (num === null || num === undefined) return null;
    return parseFloat(num).toFixed(decimals);
  };

  // Agregar campos específicos para tipo 32 según el esquema XSD oficial
  ecfData.ECF.Encabezado.Totales.ValorPagar = formatNumber(systemData.total_amount);
  ecfData.ECF.Encabezado.Totales.TotalITBISRetenido = formatNumber(systemData.taxes_amount);

  // En Factura de Consumo, el RNCComprador es opcional
  if (!ecfData.ECF.Encabezado.Comprador.RNCComprador) {
    delete ecfData.ECF.Encabezado.Comprador.RNCComprador;
  }

  return ecfData;
}

/**
 * Genera la estructura específica para Nota de Débito Electrónica (33)
 * @param {Object} systemData - Datos del sistema
 * @param {Object} baseStructure - Estructura base del ECF
 * @returns {Object} - Estructura específica para tipo 33
 */
function generate33ECF(systemData, baseStructure) {
  const ecfData = JSON.parse(JSON.stringify(baseStructure)); // Clonar estructura base
  const formatNumber = (num, decimals = 2) => {
    if (num === null || num === undefined) return null;
    return parseFloat(num).toFixed(decimals);
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return `${String(date.getDate()).padStart(2, '0')}-${String(date.getMonth() + 1).padStart(2, '0')}-${date.getFullYear()}`;
  };

  // Agregar campos específicos para tipo 33 según el esquema XSD oficial
  ecfData.ECF.Encabezado.Totales.ValorPagar = formatNumber(systemData.total_amount);
  ecfData.ECF.Encabezado.Totales.TotalITBISRetenido = formatNumber(systemData.taxes_amount);

  // Agregar sección de Información de Referencia para el e-CF modificado
  ecfData.ECF.InformacionReferencia = {
    NCFModificado: systemData.ncf_invoice_number || null,
    FechaNCFModificado: formatDate(systemData.ncf_invoice_date) || null,
    CodigoModificacion: "3" // 3: Corrige montos del NCF modificado por defecto
  };

  return ecfData;
}

/**
 * Genera la estructura específica para Nota de Crédito Electrónica (34)
 * @param {Object} systemData - Datos del sistema
 * @param {Object} baseStructure - Estructura base del ECF
 * @returns {Object} - Estructura específica para tipo 34
 */
function generate34ECF(systemData, baseStructure) {
  const ecfData = JSON.parse(JSON.stringify(baseStructure)); // Clonar estructura base
  const formatNumber = (num, decimals = 2) => {
    if (num === null || num === undefined) return null;
    return parseFloat(num).toFixed(decimals);
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return `${String(date.getDate()).padStart(2, '0')}-${String(date.getMonth() + 1).padStart(2, '0')}-${date.getFullYear()}`;
  };

  // Agregar campos específicos para tipo 34 según el esquema XSD oficial
  ecfData.ECF.Encabezado.Totales.ValorPagar = formatNumber(systemData.total_amount);
  ecfData.ECF.Encabezado.Totales.TotalITBISRetenido = formatNumber(systemData.taxes_amount);

  // Agregar sección de Información de Referencia para el e-CF modificado
  ecfData.ECF.InformacionReferencia = {
    NCFModificado: systemData.ncf_invoice_number || null,
    FechaNCFModificado: formatDate(systemData.ncf_invoice_date) || null,
    CodigoModificacion: "3" // 3: Corrige montos del NCF modificado por defecto
  };

  // Especial para Notas de Crédito: incluir IndicadorNotaCredito (calculado dinámicamente)
  // 0: si fecha de emisión del e-CF afectado es ≤ 30 días
  // 1: si fecha de emisión del e-CF afectado es > 30 días
  const emissionDays = systemData.reference_invoice_ncf?.emission_days || 0;
  ecfData.ECF.Encabezado.IdDoc.IndicadorNotaCredito = emissionDays > 30 ? "1" : "0";

  return ecfData;
}

/**
 * Genera la estructura específica para Comprobante Electrónico de Compras (41)
 * @param {Object} systemData - Datos del sistema
 * @param {Object} baseStructure - Estructura base del ECF
 * @returns {Object} - Estructura específica para tipo 41
 */
function generate41ECF(systemData, baseStructure) {
  const ecfData = JSON.parse(JSON.stringify(baseStructure)); // Clonar estructura base
  const formatNumber = (num, decimals = 2) => {
    if (num === null || num === undefined) return null;
    return parseFloat(num).toFixed(decimals);
  };

  // Agregar campos específicos para tipo 41 según el esquema XSD oficial
  ecfData.ECF.Encabezado.Totales.ValorPagar = formatNumber(systemData.total_amount);
  ecfData.ECF.Encabezado.Totales.TotalITBISRetenido = formatNumber(systemData.taxes_amount);

  return ecfData;
}

/**
 * Genera la estructura específica para Comprobante Electrónico para Gastos Menores (43)
 * @param {Object} systemData - Datos del sistema
 * @param {Object} baseStructure - Estructura base del ECF
 * @returns {Object} - Estructura específica para tipo 43
 */
function generate43ECF(systemData, baseStructure) {
  const ecfData = JSON.parse(JSON.stringify(baseStructure)); // Clonar estructura base
  const formatNumber = (num, decimals = 2) => {
    if (num === null || num === undefined) return null;
    return parseFloat(num).toFixed(decimals);
  };

  // Agregar campos específicos para tipo 43 según el esquema XSD oficial
  ecfData.ECF.Encabezado.Totales.ValorPagar = formatNumber(systemData.total_amount);
  ecfData.ECF.Encabezado.Totales.TotalITBISRetenido = formatNumber(systemData.taxes_amount);

  return ecfData;
}

/**
 * Genera la estructura específica para Comprobante Electrónico para Regímenes Especiales (44)
 * @param {Object} systemData - Datos del sistema
 * @param {Object} baseStructure - Estructura base del ECF
 * @returns {Object} - Estructura específica para tipo 44
 */
function generate44ECF(systemData, baseStructure) {
  const ecfData = JSON.parse(JSON.stringify(baseStructure)); // Clonar estructura base
  const formatNumber = (num, decimals = 2) => {
    if (num === null || num === undefined) return null;
    return parseFloat(num).toFixed(decimals);
  };

  // Agregar campos específicos para tipo 44 según el esquema XSD oficial
  ecfData.ECF.Encabezado.Totales.ValorPagar = formatNumber(systemData.total_amount);
  ecfData.ECF.Encabezado.Totales.TotalITBISRetenido = formatNumber(systemData.taxes_amount);

  return ecfData;
}

/**
 * Genera la estructura específica para Comprobante Electrónico Gubernamental (45)
 * @param {Object} systemData - Datos del sistema
 * @param {Object} baseStructure - Estructura base del ECF
 * @returns {Object} - Estructura específica para tipo 45
 */
function generate45ECF(systemData, baseStructure) {
  const ecfData = JSON.parse(JSON.stringify(baseStructure)); // Clonar estructura base
  const formatNumber = (num, decimals = 2) => {
    if (num === null || num === undefined) return null;
    return parseFloat(num).toFixed(decimals);
  };

  // Agregar campos específicos para tipo 45 según el esquema XSD oficial
  ecfData.ECF.Encabezado.Totales.ValorPagar = formatNumber(systemData.total_amount);
  ecfData.ECF.Encabezado.Totales.TotalITBISRetenido = formatNumber(systemData.taxes_amount);

  return ecfData;
}

/**
 * Genera la estructura específica para Comprobante Electrónico para Exportaciones (46)
 * @param {Object} systemData - Datos del sistema
 * @param {Object} baseStructure - Estructura base del ECF
 * @returns {Object} - Estructura específica para tipo 46
 */
function generate46ECF(systemData, baseStructure) {
  const ecfData = JSON.parse(JSON.stringify(baseStructure)); // Clonar estructura base
  const formatNumber = (num, decimals = 2) => {
    if (num === null || num === undefined) return null;
    return parseFloat(num).toFixed(decimals);
  };

  // Agregar campos específicos para tipo 46 según el esquema XSD oficial
  ecfData.ECF.Encabezado.Totales.ValorPagar = formatNumber(systemData.total_amount);
  ecfData.ECF.Encabezado.Totales.TotalITBISRetenido = formatNumber(systemData.taxes_amount);

  // CRÍTICO: Agregar campos ITBIS obligatorios para CF 46 para evitar error DGII 11300
  ecfData.ECF.Encabezado.Totales.TotalITBIS = formatNumber(systemData.taxes_amount || 0);
  ecfData.ECF.Encabezado.Totales.TotalITBIS3 = formatNumber(systemData.taxes_amount || 0);
  ecfData.ECF.Encabezado.Totales.MontoGravadoI3 = formatNumber(systemData.subtotal_amount || 0);
  ecfData.ECF.Encabezado.Totales.ITBIS3 = formatNumber(systemData.taxes_amount || 0);

  // CRÍTICO: Generar sección OtraMoneda para CF 46 (obligatoria para evitar error 11300)
  if (systemData.currency_code && systemData.currency_code !== 'DOP') {
    ecfData.ECF.Encabezado.OtraMoneda = {
      TipoMoneda: systemData.currency_code || 'USD',
      TipoCambio: formatNumber(systemData.currency_rate || 1, 4),
      MontoGravadoTotalOtraMoneda: formatNumber((systemData.subtotal_amount || 0) / (systemData.currency_rate || 1)),
      MontoGravado3OtraMoneda: formatNumber((systemData.subtotal_amount || 0) / (systemData.currency_rate || 1)),
      TotalITBISOtraMoneda: formatNumber((systemData.taxes_amount || 0) / (systemData.currency_rate || 1)),
      TotalITBIS3OtraMoneda: formatNumber((systemData.taxes_amount || 0) / (systemData.currency_rate || 1)),
      MontoTotalOtraMoneda: formatNumber((systemData.total_amount || 0) / (systemData.currency_rate || 1))
      // NOTA: MontoExentoOtraMoneda NO se incluye para CF 46 para evitar error 11300
    };
  }

  // Agregar secciones específicas para exportaciones
  ecfData.ECF.Encabezado.InformacionesAdicionales = {
    NombrePuertoEmbarque: "PUERTO CAUCEDO",
    CondicionesEntrega: "FOB",
    TotalFob: formatNumber(systemData.subtotal_amount)
  };

  ecfData.ECF.Encabezado.Transporte = {
    ViaTransporte: "01", // Marítimo
    PaisOrigen: "REPÚBLICA DOMINICANA",
    PaisDestino: "ESTADOS UNIDOS"
  };

  // Agregar campo PaisComprador en la sección Comprador
  ecfData.ECF.Encabezado.Comprador.PaisComprador = "ESTADOS UNIDOS";

  return ecfData;
}

/**
 * Genera la estructura específica para Comprobante Electrónico para Pagos al Exterior (47)
 * @param {Object} systemData - Datos del sistema
 * @param {Object} baseStructure - Estructura base del ECF
 * @returns {Object} - Estructura específica para tipo 47
 */
function generate47ECF(systemData, baseStructure) {
  const ecfData = JSON.parse(JSON.stringify(baseStructure)); // Clonar estructura base
  const formatNumber = (num, decimals = 2) => {
    if (num === null || num === undefined) return null;
    return parseFloat(num).toFixed(decimals);
  };

  // Agregar campos específicos para tipo 47 según el esquema XSD oficial
  ecfData.ECF.Encabezado.Totales.ValorPagar = formatNumber(systemData.total_amount);
  ecfData.ECF.Encabezado.Totales.TotalISRRetencion = formatNumber(systemData.taxes_amount * 0.27); // 27% de retención ISR

  return ecfData;
}

/**
 * Llena los detalles de ítems en la estructura ECF
 * @param {Object} systemData - Datos del sistema
 * @param {Object} ecfData - Estructura ECF
 * @returns {Object} - Estructura ECF con detalles de ítems
 */
function fillItemDetails(systemData, ecfData) {
  const formatNumber = (num, decimals = 2) => {
    if (num === null || num === undefined) return null;
    return parseFloat(num).toFixed(decimals);
  };

  // Verificar si hay detalles de ítems
  if (!systemData.invoice_details || !Array.isArray(systemData.invoice_details)) {
    // Si no hay detalles, crear un ítem por defecto
    ecfData.ECF.DetallesItems.Item = [{
      NumeroLinea: 1,
      IndicadorFacturacion: 1,
      NombreItem: "Ítem por defecto",
      IndicadorBienoServicio: 1,
      CantidadItem: "1.00",
      PrecioUnitarioItem: formatNumber(systemData.subtotal_amount),
      MontoItem: formatNumber(systemData.subtotal_amount)
    }];
    return ecfData;
  }

  // Procesar cada ítem
  ecfData.ECF.DetallesItems.Item = systemData.invoice_details.map((item, index) => {
    // Crear estructura del ítem
    const itemData = {
      NumeroLinea: index + 1,
      IndicadorFacturacion: 1, // 1: ITBIS tasa 1 (18%) por defecto
      NombreItem: item.item_name || "",
      IndicadorBienoServicio: 1, // 1: Bien, 2: Servicio
      CantidadItem: formatNumber(item.quantity || 1, 2),
      PrecioUnitarioItem: formatNumber(item.price || 0, 2),
      MontoItem: formatNumber(item.total_amount || (item.quantity * item.price), 2),
      UnidadMedida: "31" // Unidades
    };

    return itemData;
  });

  return ecfData;
}

/**
 * Limpia la estructura e-CF eliminando campos vacíos o innecesarios
 * @param {Object} ecfData - Estructura e-CF con datos
 * @returns {Object} - Estructura e-CF limpia
 */
function cleanECFStructure(ecfData) {
  // Función recursiva para limpiar objetos
  const cleanObject = (obj) => {
    if (obj === null || typeof obj !== 'object') return obj;

    // Si es un array, limpiar cada elemento
    if (Array.isArray(obj)) {
      const newArray = obj.map(item => cleanObject(item)).filter(item => {
        if (item === null || item === undefined) return false;
        if (typeof item === 'object' && Object.keys(item).length === 0) return false;
        return true;
      });

      return newArray.length > 0 ? newArray : undefined;
    }

    // Para objetos normales
    const newObj = {};
    let isEmpty = true;

    for (const key in obj) {
      const value = cleanObject(obj[key]);

      if (value !== null && value !== undefined && value !== '') {
        newObj[key] = value;
        isEmpty = false;
      }
    }

    return isEmpty ? undefined : newObj;
  };

  // Limpiar estructura completa
  const cleanedData = cleanObject(ecfData);

  // Si después de la limpieza no hay estructura, devolver al menos la estructura mínima requerida
  if (!cleanedData || !cleanedData.ECF) {
    return {
      ECF: {
        Encabezado: {
          Version: "1.0",
          IdDoc: {
            TipoeCF: ecfData.ECF.Encabezado.IdDoc.TipoeCF || "31",
            eNCF: ecfData.ECF.Encabezado.IdDoc.eNCF || "E310000000001"
          },
          Emisor: {
            RNCEmisor: ecfData.ECF.Encabezado.Emisor.RNCEmisor || "",
            RazonSocialEmisor: ecfData.ECF.Encabezado.Emisor.RazonSocialEmisor || "",
            FechaEmision: ecfData.ECF.Encabezado.Emisor.FechaEmision || "01-01-2025"
          },
          Comprador: {
            RNCComprador: ecfData.ECF.Encabezado.Comprador.RNCComprador || "",
            RazonSocialComprador: ecfData.ECF.Encabezado.Comprador.RazonSocialComprador || ""
          },
          Totales: {
            MontoTotal: ecfData.ECF.Encabezado.Totales.MontoTotal || "0.00"
          }
        },
        DetallesItems: {
          Item: [{
            NumeroLinea: 1,
            IndicadorFacturacion: 1,
            NombreItem: "Ítem por defecto",
            IndicadorBienoServicio: 1,
            CantidadItem: "1.00",
            PrecioUnitarioItem: "0.00",
            MontoItem: "0.00"
          }]
        },
        FechaHoraFirma: ecfData.ECF.FechaHoraFirma || "01-01-2025 00:00:00"
      }
    };
  }

  return cleanedData;
}

// Exportar la función principal para su uso en otros archivos
export default processECFByType;